import { forwardRef, useEffect, useImperativeHandle, useState, type ChangeEvent } from 'react';

import { useTranslation } from 'react-i18next';

import {
  DEFAULT_COUNTRY_CODE,
  EIN_DOCUMENT_TYPE,
  MIN_HEADCOUNT_FOR_BENEFITS,
  SALES_MODEL_COPY,
  SALES_MODEL_ORDER
} from '../../../constants/prospects';
import { CLICK_ACTION, CREATE_PROSPECT, SALES_MODEL_PARAM } from '../../../constants/tagManager';
import type { IProspect, IUpdateProspectInput } from '../../../interfaces/prospects';
import TagManagerService from '../../../services/gtm/tagManagerService';
import { TRANSLATIONS_GLOBAL } from '../../../translations/commons';
import { TRANSLATIONS_PROSPECTS } from '../../../translations/prospects';
import type { ProspectSalesModel } from '../../../types/prospects';
import {
  EMPTY_FIELD,
  formatProspectAddress,
  getSalesModel,
  requiresHeadcountMinimum
} from '../../../utils/prospects/prospects';
import { isDraftEqual, trimmedOrUndefined } from '../../../utils/utils';
import CustomInput from '../../Inputs/CustomInput/CustomInput';
import CustomSelect from '../../Inputs/CustomSelect/CustomSelect';
import PanelRow from '../PanelRow/PanelRow';

export interface ICompanyCardHandle {
  save: () => Promise<void>;
  getSalesModel: () => ProspectSalesModel;
  resetSalesModel: () => void;
}

interface ICompanyCardProps {
  prospect: IProspect;
  onUpdate: (input: IUpdateProspectInput) => Promise<void>;
  onDraftChange?: (overrides: Partial<IProspect> | null) => void;
  editing: boolean;
  onValidChange: (valid: boolean) => void;
  /** Saved quotes a sales model change would discard. Only used to warn inline. */
  savedQuotesCount?: number;
  /** Reports whether the headcount gate is locking the rest of the form. */
  onLockedChange?: (locked: boolean) => void;
  /** Reports why saving is blocked, so the parent can explain it on the save button. */
  onSaveBlockerChange?: (blocker?: string) => void;
}

interface ICompanyDraft {
  companyName: string;
  workforceSize: string;
  salesModel: ProspectSalesModel;
  documentValue: string;
  industry: string;
  street: string;
  addressLine1: string;
  city: string;
  state: string;
  zipCode: string;
}

const draftFromProspect = (prospect: IProspect): ICompanyDraft => ({
  companyName: prospect.companyName,
  workforceSize: String(prospect.workforceSize),
  salesModel: getSalesModel(prospect),
  documentValue: prospect.documentValue ?? '',
  industry: prospect.industry ?? '',
  street: prospect.street ?? '',
  addressLine1: prospect.addressLine1 ?? '',
  city: prospect.city ?? '',
  state: prospect.state ?? '',
  zipCode: prospect.zipCode ?? ''
});

const CompanyCard = forwardRef<ICompanyCardHandle, ICompanyCardProps>(
  (
    {
      prospect,
      onUpdate,
      onDraftChange,
      editing,
      onValidChange,
      savedQuotesCount = 0,
      onLockedChange,
      onSaveBlockerChange
    },
    ref
  ) => {
    const { t } = useTranslation();
    const [draft, setDraft] = useState<ICompanyDraft>(() => draftFromProspect(prospect));

    useEffect(() => {
      if (!editing) {
        onDraftChange?.(null);
        return;
      }
      onDraftChange?.({
        documentValue: draft.documentValue.trim() || undefined,
        industry: draft.industry.trim() || undefined,
        street: draft.street.trim() || undefined,
        addressLine1: draft.addressLine1.trim() || undefined,
        city: draft.city.trim() || undefined,
        state: draft.state.trim() || undefined,
        zipCode: draft.zipCode.trim() || undefined
      });
    }, [editing, draft, onDraftChange]);

    const savedSalesModel = getSalesModel(prospect);
    const workforceNum = Number(draft.workforceSize);
    const isWorkforceFilled = draft.workforceSize !== '' && Number.isInteger(workforceNum) && workforceNum > 0;

    const requiresCarrierMinimum = requiresHeadcountMinimum(draft.salesModel);
    const meetsCarrierMinimum = !requiresCarrierMinimum || workforceNum >= MIN_HEADCOUNT_FOR_BENEFITS;
    const isWorkforceValid = isWorkforceFilled && meetsCarrierMinimum;

    const isLockedByHeadcount = !isWorkforceValid;
    const showFieldErrors = !isLockedByHeadcount;
    const headcountErrorKey = requiresCarrierMinimum
      ? TRANSLATIONS_PROSPECTS.profile.headcountMinimum
      : TRANSLATIONS_PROSPECTS.profile.headcountRequired;
    const headcountError = isWorkforceValid ? undefined : t(headcountErrorKey, { count: MIN_HEADCOUNT_FOR_BENEFITS });
    const hasQuotesAtRisk = savedQuotesCount > 0 && draft.salesModel !== savedSalesModel;

    useEffect(() => {
      onLockedChange?.(isLockedByHeadcount);
    }, [isLockedByHeadcount, onLockedChange]);
    const isCompanyNameValid = draft.companyName.trim() !== '';
    const isDocumentValueValid = draft.documentValue.trim() !== '';
    const isIndustryValid = draft.industry.trim() !== '';
    const isStreetValid = draft.street.trim() !== '';
    const isCityValid = draft.city.trim() !== '';
    const isStateValid = draft.state.trim() !== '';
    const isZipCodeValid = draft.zipCode.trim() !== '';
    const isValid =
      isWorkforceValid &&
      isCompanyNameValid &&
      isDocumentValueValid &&
      isIndustryValid &&
      isStreetValid &&
      isCityValid &&
      isStateValid &&
      isZipCodeValid;

    useEffect(() => {
      onValidChange(isValid);
    }, [isValid, onValidChange]);

    const saveBlocker = (() => {
      if (isWorkforceValid) return undefined;
      if (!isWorkforceFilled) return t(TRANSLATIONS_PROSPECTS.profile.headcountRequired);
      return t(TRANSLATIONS_PROSPECTS.profile.tooltips.headcountMinimum, { count: MIN_HEADCOUNT_FOR_BENEFITS });
    })();

    useEffect(() => {
      onSaveBlockerChange?.(saveBlocker);
    }, [saveBlocker, onSaveBlockerChange]);

    useImperativeHandle(
      ref,
      () => ({
        getSalesModel: () => draft.salesModel,
        resetSalesModel: () => setDraft((prev) => ({ ...prev, salesModel: getSalesModel(prospect) })),
        save: async () => {
          if (isDraftEqual(draft, draftFromProspect(prospect))) return;
          const workforceSize = Number.parseInt(draft.workforceSize, 10);
          await onUpdate({
            companyName: trimmedOrUndefined(draft.companyName),
            workforceSize: Number.isNaN(workforceSize) ? undefined : workforceSize,
            salesModel: draft.salesModel,
            countryCode: DEFAULT_COUNTRY_CODE,
            documentValue: trimmedOrUndefined(draft.documentValue),
            documentType: prospect.documentType ?? EIN_DOCUMENT_TYPE,
            industry: trimmedOrUndefined(draft.industry),
            street: trimmedOrUndefined(draft.street),
            addressLine1: trimmedOrUndefined(draft.addressLine1),
            city: trimmedOrUndefined(draft.city),
            state: trimmedOrUndefined(draft.state),
            zipCode: trimmedOrUndefined(draft.zipCode)
          });
          const salesModelChanged = draft.salesModel !== getSalesModel(prospect);
          TagManagerService.pushEvent(
            CLICK_ACTION,
            CREATE_PROSPECT,
            salesModelChanged ? { salesModel: SALES_MODEL_PARAM[draft.salesModel] } : {}
          );
        }
      }),
      [draft, prospect, onUpdate]
    );

    const set = <K extends keyof ICompanyDraft>(key: K, value: ICompanyDraft[K]) =>
      setDraft((prev) => ({ ...prev, [key]: value }));

    const requiredLabel = (label: string) => (
      <>
        {label} <span className="profile-form__required">{t(TRANSLATIONS_GLOBAL.required)}</span>
      </>
    );

    const address = formatProspectAddress(prospect);
    const rows = [
      { label: t(TRANSLATIONS_PROSPECTS.profile.fields.legalName), value: prospect.companyName },
      { label: t(TRANSLATIONS_PROSPECTS.profile.fields.employees), value: String(prospect.workforceSize) },
      {
        label: t(TRANSLATIONS_PROSPECTS.profile.fields.whatYouOffer),
        value: t(SALES_MODEL_COPY[getSalesModel(prospect)].title)
      },
      { label: t(TRANSLATIONS_PROSPECTS.profile.fields.taxId), value: prospect.documentValue || EMPTY_FIELD },
      { label: t(TRANSLATIONS_PROSPECTS.profile.fields.address), value: address || EMPTY_FIELD },
      { label: t(TRANSLATIONS_PROSPECTS.profile.fields.industry), value: prospect.industry || EMPTY_FIELD }
    ];

    if (!editing) {
      return (
        <>
          {rows.map(({ label, value }) => (
            <PanelRow key={label} label={label} value={value} empty={value === EMPTY_FIELD} />
          ))}
        </>
      );
    }

    return (
      <div className="profile-form">
        <div className="profile-form__row">
          <CustomInput
            label={t(TRANSLATIONS_PROSPECTS.profile.fields.legalName)}
            placeholder={t(TRANSLATIONS_PROSPECTS.forms.companyName.placeholder)}
            value={draft.companyName}
            onChange={(event) => set('companyName', event.target.value)}
            disabled={isLockedByHeadcount}
            maxLength={50}
          />
          <CustomInput
            type="number"
            label={t(TRANSLATIONS_PROSPECTS.profile.fields.employees)}
            placeholder={t(TRANSLATIONS_PROSPECTS.forms.workforceSize.placeholder)}
            value={draft.workforceSize}
            onChange={(event) => set('workforceSize', event.target.value)}
            error={headcountError}
            errorSeverity="danger"
          />
        </div>
        <div className="profile-form__sales-model">
          <CustomSelect
            label={t(TRANSLATIONS_PROSPECTS.profile.fields.whatYouOffer)}
            value={draft.salesModel}
            onChange={(event) => set('salesModel', event.target.value as ProspectSalesModel)}
            options={SALES_MODEL_ORDER.map((model) => ({
              value: model,
              label: t(SALES_MODEL_COPY[model].title)
            }))}
            helperText={
              hasQuotesAtRisk
                ? t(TRANSLATIONS_PROSPECTS.profile.salesModel.discardNote, { count: savedQuotesCount })
                : undefined
            }
          />
        </div>
        <div className="profile-form__row">
          <CustomInput
            label={requiredLabel(t(TRANSLATIONS_PROSPECTS.forms.documentValue.label))}
            placeholder="XX-XXXXXXX"
            value={draft.documentValue}
            error={showFieldErrors && !draft.documentValue.trim()}
            onChange={(event) => set('documentValue', event.target.value)}
            disabled={isLockedByHeadcount}
            maxLength={10}
          />
          <CustomInput
            label={requiredLabel(t(TRANSLATIONS_PROSPECTS.forms.industry.label))}
            placeholder={t(TRANSLATIONS_PROSPECTS.forms.industry.placeholder)}
            value={draft.industry}
            error={showFieldErrors && !draft.industry.trim()}
            onChange={(event) => set('industry', event.target.value)}
            disabled={isLockedByHeadcount}
            maxLength={50}
          />
        </div>
        <CustomInput
          label={requiredLabel(t(TRANSLATIONS_PROSPECTS.forms.street.label))}
          placeholder={t(TRANSLATIONS_PROSPECTS.forms.street.placeholder)}
          value={draft.street}
          error={showFieldErrors && !draft.street.trim()}
          onChange={(event) => set('street', event.target.value)}
          disabled={isLockedByHeadcount}
          maxLength={100}
        />
        <CustomInput
          label={t(TRANSLATIONS_PROSPECTS.forms.addressLine1.label)}
          placeholder={t(TRANSLATIONS_PROSPECTS.forms.addressLine1.placeholder)}
          value={draft.addressLine1}
          onChange={(event) => set('addressLine1', event.target.value)}
          disabled={isLockedByHeadcount}
          maxLength={50}
        />
        <div className="profile-form__row">
          <CustomInput
            label={requiredLabel(t(TRANSLATIONS_PROSPECTS.forms.city.label))}
            placeholder={t(TRANSLATIONS_PROSPECTS.forms.city.placeholder)}
            value={draft.city}
            error={showFieldErrors && !draft.city.trim()}
            onChange={(event) => set('city', event.target.value)}
            disabled={isLockedByHeadcount}
            maxLength={50}
          />
          <div className="profile-form__state-zip">
            <CustomInput
              label={requiredLabel(t(TRANSLATIONS_PROSPECTS.forms.state.label))}
              placeholder={t(TRANSLATIONS_PROSPECTS.forms.state.placeholder)}
              maxLength={2}
              value={draft.state}
              error={showFieldErrors && !draft.state.trim()}
              onChange={(event: ChangeEvent<HTMLInputElement>) => set('state', event.target.value.toUpperCase())}
              disabled={isLockedByHeadcount}
            />
            <CustomInput
              label={requiredLabel(t(TRANSLATIONS_PROSPECTS.forms.zipCode.label))}
              placeholder={t(TRANSLATIONS_PROSPECTS.forms.zipCode.placeholder)}
              maxLength={5}
              value={draft.zipCode}
              error={showFieldErrors && !draft.zipCode.trim()}
              onChange={(event: ChangeEvent<HTMLInputElement>) => set('zipCode', event.target.value.replace(/\D/g, ''))}
              disabled={isLockedByHeadcount}
            />
          </div>
        </div>
      </div>
    );
  }
);

CompanyCard.displayName = 'CompanyCard';
export default CompanyCard;
